import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  ReactElement,
  useMemo,
} from "react";
import classnames from "classnames";
import uniqueId from "lodash.uniqueid";
import { usePrevious } from "../utils/usePrevious";
import { AutoSuggestProps, Term } from "./auto-suggest.types";
import useDeepCompareEffect from "../utils/useDeepCompareEffect";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../utils/SSR/useSSR";

export const AutoSuggest = ({
  className,
  ariaLabelledBy,
  id,
  terms: termsProp = [],
  label,
  showAllTerms,
  value: valueProp,
  defaultValue,
  onChange,
  onSelection,
  showNoResultsFound = false,
  minLength = 2,
  maxSuggestions = 7,
  noResultsFoundText = "No results found",
  customGetSuggestions,
  customRenderSuggestions,
  statusMessage,
  status,
  ...rest
}: AutoSuggestProps): ReactElement => {
  useSSR(() => isPropEmpty(id) && showSSRHydrationWarning("id", "AutoSuggest"));
  // Transform simple `terms` format string[] to object Term[] used in advanced variants
  const transformToObject = (termsProp: Term[] | string[]): Term[] => {
    return termsProp.map((term: Term | string): Term => {
      if (typeof term === "string") {
        return { value: term };
      }
      return term;
    });
  };

  // Keep terms in state because object changes while typing suggestions
  // and we need to prevent overriding terms by termsProp
  const [terms, setTerms] = useState(transformToObject(termsProp));

  // Update terms only if termsProp changes
  useDeepCompareEffect(() => {
    setTerms(transformToObject(termsProp));
  }, [termsProp]);

  // Property defines the index of a selected suggestion
  const [activeSuggestion, setActiveSuggestion] = useState<number>();

  const prevActiveSuggestion = usePrevious(activeSuggestion);

  // Property defines the if the input is focused
  const [isFocused, setFocused] = useState(false);

  // Property will determine whether the suggestion list appears
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Property will determine whether to add 'show-all-terms' class or not
  const [showingAllTerms, setShowingAllTerms] = useState(false);

  // Set to an empty array, matches the user’s input
  const [suggestionResults, setSuggestionResults] = useState<Term[]>([]);

  const { current: uniqueControlId } = useRef(uniqueId("autosuggest-listbox_"));
  const controlId = id || uniqueControlId;

  const { current: uniqueSpanId } = useRef(uniqueId("autosuggest-label_"));
  const spanId = id ? `${id}-label` : uniqueSpanId;

  // Property assigns itself an empty string for accepting a word from a user’s input`
  const [valueState, setValueState] = useState<string>(defaultValue || "");

  const { current: isControlled } = useRef(valueProp !== undefined);
  const value = isControlled ? valueProp : valueState;

  const [ariaSelected] = useState(false);
  const [ariaActiveDescendant, setAriaActiveDescendant] = useState("");

  // Refs
  const autoSuggestRef = useRef<HTMLDivElement>(null);
  const newSuggestionRef = useRef<HTMLUListElement>(null);
  const inputEl = useRef<HTMLInputElement>(null);
  const clearBtn = useRef<HTMLButtonElement>(null);

  // Classes
  const autoSuggestClasses = classnames(
    "spark-auto-suggest",
    { active: value || isFocused },
    className
  );
  const clearBtnClasses = classnames(
    "spark-auto-suggest__clear-btn spark-icon-close",
    { visible: value }
  );
  const listBoxClasses = classnames(
    "spark-auto-suggest__listbox",
    { visible: showSuggestions && suggestionResults.length > 0 },
    { "show-all-terms": showingAllTerms }
  );

  /** getSuggestionResults is a function responsible for filtering and mapping
   * of all the terms to show only those that are matching search criteria.
   * @param inputVal  the value to determine search criteria
   * @returns {{label: (string|JSX.Element)[], value: *}[]|*}
   */
  const getSuggestionResults = useMemo(
    () =>
      (inputVal: string): Term[] => {
        if (typeof customGetSuggestions === "function") {
          return customGetSuggestions(terms, inputVal);
        }

        return terms
          .filter((term) => {
            // deprecated term.label support - to remove in v2
            const termValue = term.label || term.value;
            return typeof termValue === "string"
              ? termValue.toLowerCase().indexOf(inputVal.toLowerCase()) !== -1
              : false;
          })
          .map((suggestion) => {
            // deprecated term.label support - to remove in v2
            const suggestionValue = suggestion.label || suggestion.value;

            const escapeRegExp = (str: string): string =>
              str.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");

            const regex = new RegExp(`(${escapeRegExp(inputVal)})`, "i");
            const parts = String(suggestionValue).split(regex);

            const newParts = parts.map((part, index) =>
              regex.test(part) ? <mark key={index}>{part}</mark> : part
            );

            return {
              value: suggestion.value,
              valueFormatted: newParts,
            };
          });
      },
    [customGetSuggestions, terms]
  );

  /**
   * When the input receives a value or the value is about to change, update the displayed suggestions
   * @param {Object} e
   */
  const _onInput: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ): void => {
    const inputVal = event.currentTarget.value;

    setValueState(inputVal);

    if (inputVal.length < minLength) {
      setShowSuggestions(false);

      if (showAllTerms && inputVal.length === 0) {
        resetSuggestions();
      } else {
        setShowingAllTerms(false);
      }
    }

    const _suggestionResults = getSuggestionResults(inputVal);

    if (showAllTerms) {
      setSuggestionResults(_suggestionResults);
    } else {
      setSuggestionResults(_suggestionResults.slice(0, maxSuggestions));
    }

    setShowSuggestions(true);

    if (!suggestionResults.length) {
      setShowSuggestions(false);
    }

    if (showNoResultsFound) {
      setShowSuggestions(true);
    }

    onChange?.(event, inputVal);
  };

  const _handleSelection = (
    event: React.MouseEvent<HTMLLIElement> | React.KeyboardEvent<HTMLLIElement>,
    term: Term
  ): void => {
    const input = inputEl.current;
    const button = clearBtn.current;
    const text = event?.currentTarget?.innerText || term.value;
    const value = term.value || event?.currentTarget?.innerText;
    setActiveSuggestion(undefined);
    setValueState(text);
    if (input) {
      input.classList.add("focus");
      input.focus();
    }
    if (button) button.classList.add("visible");
    setAriaActiveDescendant("");
    setShowSuggestions(false);
    onChange?.(event, value);
    onSelection?.(event, value, term);
  };

  // When the window is clicked and the area clicked is not part of the Auto Suggest, close the suggestions list
  // If an item in the suggestions list is clicked, set it as the active/selected item
  const _onWindowClick = useCallback(
    (event: any) => {
      const input = inputEl.current;
      // Check if we have clicked outside of the Auto Suggest component and collapse the Suggestions list if so
      if (
        autoSuggestRef.current &&
        !autoSuggestRef.current.contains(event.target)
      ) {
        input?.classList.remove("focus");
        setFocused(false);
        setShowSuggestions(false);

        const compareValue = input?.value;
        const filteredResults = suggestionResults.filter(
          (result) => result.value === compareValue
        );
        setSuggestionResults(filteredResults);
      }
    },
    [suggestionResults]
  );

  // When the Clear Button is clicked, clear the entered value and close the listbox if it is open
  // If showAllTerms is true, display all the terms once the field is clear
  const onClearClick: React.MouseEventHandler<HTMLButtonElement> = (
    event
  ): void => {
    setValueState("");
    const input = inputEl.current;
    if (input) input.focus();
    setShowSuggestions(false);

    const suggestionRef = newSuggestionRef.current;
    if (suggestionRef) {
      const prevActiveSuggestionElement = suggestionRef.querySelectorAll(
        "li:not([role=presentation])"
      )[prevActiveSuggestion];
      prevActiveSuggestionElement?.classList.remove("focus");
    }

    if (showAllTerms) {
      resetSuggestions();
    }

    onChange?.(event, "");
    onSelection?.(event, "");
  };

  const resetSuggestions = useCallback(() => {
    setSuggestionResults(terms);
    setShowingAllTerms(true);
    setShowSuggestions(true);
  }, [terms]);

  useEffect(() => {
    if (showSuggestions) {
      let removeFocusIndex: number;

      if (activeSuggestion !== undefined) {
        if (prevActiveSuggestion < activeSuggestion) {
          removeFocusIndex = activeSuggestion - 1;
        } else {
          removeFocusIndex = activeSuggestion + 1;
        }

        const suggestionRef = newSuggestionRef.current;
        const currentSuggestion =
          suggestionRef?.querySelectorAll("li:not([role=presentation])")[
            removeFocusIndex
          ] || undefined;

        if (currentSuggestion !== undefined) {
          currentSuggestion.classList.remove("focus");
          currentSuggestion.setAttribute("aria-selected", "false");
        }

        const newSuggestion =
          suggestionRef?.querySelectorAll("li:not([role=presentation])")[
            activeSuggestion
          ] || undefined;
        if (newSuggestion !== undefined) {
          newSuggestion.classList.add("focus");
          newSuggestion.setAttribute("aria-selected", "true");
          (newSuggestion as HTMLLIElement).focus();
        }
      }
    }
  }, [activeSuggestion, showSuggestions, prevActiveSuggestion]);

  useEffect(() => {
    const autoSuggestContainerRef = autoSuggestRef.current;
    const autoSuggestInputRef = inputEl.current;

    /**
     * When a key is pressed on the window and it's an ESC, close the suggestions list
     * @param {Object} e
     */
    const _onKeyup = (event: KeyboardEvent): void => {
      if (
        event.key === "Escape" &&
        document.activeElement === inputEl.current
      ) {
        setShowSuggestions(false);
        setValueState("");
        onChange?.(event, "");
      }
      if (event.key === "Escape" && showAllTerms) {
        resetSuggestions();
      }
    };

    // When the input element gains focus.
    const _onFocus = (): void => {
      const input = inputEl.current;
      if (!input) return;
      input.classList.add("focus");
      input.focus();
      setFocused(true);

      if (input.value.length > 0) {
        const _suggestionResults = getSuggestionResults(input.value);
        if (showAllTerms) {
          setSuggestionResults(_suggestionResults);
        } else {
          setSuggestionResults(_suggestionResults.slice(0, maxSuggestions));
        }
        setShowSuggestions(true);
      } else if (!showAllTerms) {
        setShowSuggestions(false);
      } else {
        resetSuggestions();
      }
    };

    /**
     * When a key is pressed and it is the Enter key, set the current suggestion as the selected one.
     * If it is the tab key, determine where to shift focus and whether to manage the component's display classes
     * If it is the arrow keys, navigate the suggestions list
     * @param {Object} e
     */
    const _onKeyDown = (event: KeyboardEvent): void => {
      if (suggestionResults.length < 1) {
        return;
      }
      const input = inputEl.current;
      const button = clearBtn.current;
      const suggestionsRef = newSuggestionRef.current;

      // User pressed the enter key, update the input and close the suggestions
      if (event.key === "Enter") {
        setActiveSuggestion(undefined);
        if (input) {
          input.classList.add("focus");
          input.focus();
        }
        button?.classList.add("visible");
        setAriaActiveDescendant("");
        setShowSuggestions(false);

        if (prevActiveSuggestion !== undefined && suggestionsRef) {
          const prevActiveSuggestionElement = suggestionsRef.querySelectorAll(
            "li:not([role=presentation])"
          )[prevActiveSuggestion];
          prevActiveSuggestionElement?.classList.remove("focus");
        }
      }

      // Set focus back to input if printable keys are pressed
      if (
        event.key !== "Enter" &&
        event.key !== "ArrowUp" &&
        event.key !== "ArrowDown" &&
        input &&
        document.activeElement !== input &&
        document.activeElement !== clearBtn.current
      ) {
        input.focus();
        return;
      }

      // Remove focus from component if navigating away from it via Tab key
      if (
        event.key === "Tab" &&
        (document.activeElement === button || event.key === "Tab") &&
        (event.target as HTMLInputElement)?.value.length === 0
      ) {
        setShowSuggestions(false);
        input?.classList.remove("focus");
        setFocused(false);

        if (showAllTerms) {
          setShowingAllTerms(false);
        }
      }

      // Up arrow
      if (event.key === "ArrowUp") {
        event.preventDefault();

        if (activeSuggestion === 0 || activeSuggestion === undefined) {
          return;
        }

        const prevSuggestion = activeSuggestion - 1;

        setActiveSuggestion(prevSuggestion);
        setAriaActiveDescendant(
          "spark-auto-suggest__list-item--" + prevSuggestion
        );
        setValueState(suggestionResults[prevSuggestion].value);
      }

      // Down arrow
      if (event.key === "ArrowDown") {
        event.preventDefault();

        if (activeSuggestion === undefined) {
          setActiveSuggestion(0);
          setValueState(suggestionResults[0].value);

          setAriaActiveDescendant("spark-auto-suggest__list-item--0");
        } else if (activeSuggestion < suggestionResults.length - 1) {
          setActiveSuggestion(activeSuggestion + 1);
          const nextSuggestion = activeSuggestion + 1;
          setAriaActiveDescendant(
            "spark-auto-suggest__list-item--" + nextSuggestion
          );
          setValueState(suggestionResults[nextSuggestion].value);
        }
      }
    };

    window.addEventListener("click", _onWindowClick);
    window.addEventListener("keyup", _onKeyup);
    // const input = inputEl.current;
    // const autoSuggestion = autoSuggestRef.current;
    autoSuggestContainerRef?.addEventListener("keydown", _onKeyDown);
    autoSuggestInputRef?.addEventListener("focus", _onFocus);

    return () => {
      window.removeEventListener("click", _onWindowClick);
      window.removeEventListener("keyup", _onKeyup);
      autoSuggestContainerRef?.removeEventListener("keydown", _onKeyDown);
      autoSuggestInputRef?.removeEventListener("focus", _onFocus);
    };
  }, [
    suggestionResults,
    activeSuggestion,
    terms,
    showAllTerms,
    resetSuggestions,
    _onWindowClick,
    showSuggestions,
    value,
    prevActiveSuggestion,
    onChange,
    getSuggestionResults,
    maxSuggestions,
  ]);

  useEffect(() => {
    setSuggestionResults(terms);
  }, [terms]);

  const renderSuggestions = (
    suggestionResults: Term[]
  ):
    | AutoSuggestProps["customRenderSuggestions"]
    | ReactElement
    | ReactElement[]
    | null => {
    if (suggestionResults.length !== 0) {
      if (customRenderSuggestions) {
        return customRenderSuggestions(
          suggestionResults,
          _handleSelection,
          ariaSelected,
          terms
        );
      }

      return suggestionResults.map((term: Term, index: number) => {
        return (
          <li
            role="option"
            id={"spark-auto-suggest__list-item--" + index}
            aria-selected={ariaSelected}
            className="spark-auto-suggest__list-item"
            tabIndex={-1}
            key={term.value}
            onClick={(event): void => _handleSelection(event, term)}
            onKeyDown={(event): void => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                _handleSelection(event, term);
              }
            }}
          >
            {term.valueFormatted ?? term.value}
          </li>
        );
      });
    } else if (showNoResultsFound) {
      return (
        <li
          role="presentation"
          className="spark-auto-suggest__list-item spark-auto-suggest__list-item--no-results"
        >
          {noResultsFoundText}
        </li>
      );
    }

    return null;
  };

  const statusAttr =
    statusMessage && status ? { [`data-${status}`]: true } : null;

  return (
    <div ref={autoSuggestRef} className={autoSuggestClasses} {...statusAttr}>
      <label>
        <input
          ref={inputEl}
          role="combobox"
          aria-expanded={showSuggestions && suggestionResults.length > 0}
          aria-haspopup="listbox"
          className="spark-auto-suggest__field"
          type="text"
          onChange={_onInput}
          value={value}
          autoComplete="off"
          aria-labelledby={ariaLabelledBy || spanId}
          aria-autocomplete="list"
          aria-controls={controlId}
          aria-activedescendant={ariaActiveDescendant}
          {...rest}
        />
        <span id={spanId} className="spark-label">
          {label}
        </span>
        <button
          ref={clearBtn}
          className={clearBtnClasses}
          onClick={onClearClick}
          type="button"
          aria-label="Clear contents"
        ></button>
      </label>
      <ul
        ref={newSuggestionRef}
        id={controlId}
        className={listBoxClasses}
        role="listbox"
      >
        {renderSuggestions(suggestionResults) as ReactElement}
      </ul>
      {statusMessage && (
        <span className="spark-input__message">{statusMessage}</span>
      )}
    </div>
  );
};

AutoSuggest.displayName = "AutoSuggest";
